import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { EntityLink } from '@/components/EntityLink';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Plus, Search, ChevronLeft, ChevronRight, Pencil, ClipboardList, Trash2, PauseCircle, PlayCircle } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

export default function RoutesPage() {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const limit = 25;

  useEffect(() => { loadRoutes(); }, [page, search]);

  const loadRoutes = async () => {
    setLoading(true);
    try {
      const params = { page, limit, include_all_statuses: true };
      if (search.trim()) params.search = search.trim();
      const res = await inventoryAPI.listRoutes(params);
      const data = res.data;
      // Support both old (array) and new (paginated) response
      if (Array.isArray(data)) {
        setRoutes(data);
        setTotal(data.length);
      } else {
        setRoutes(data.routes || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      toast.error('Failed to load routes');
    } finally { setLoading(false); }
  };

  const totalPages = Math.ceil(total / limit);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await inventoryAPI.deleteRoute(deleteTarget.id);
      toast.success(`Route "${deleteTarget.route_name}" deleted`);
      setDeleteTarget(null);
      loadRoutes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete route');
      setDeleteTarget(null);
    }
  };

  const handleSuspend = async (route) => {
    try {
      const res = await inventoryAPI.suspendRoute(route.id);
      const newStatus = res.data.status;
      toast.success(`Route "${route.route_name}" ${newStatus === 'suspended' ? 'suspended' : 'reactivated'}`);
      loadRoutes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update route status');
    }
  };

  return (
    <div className="space-y-4" data-testid="routes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Routes & BOM</h1>
          <p className="text-xs text-muted-foreground">{total} routes defined</p>
        </div>
        <Button size="sm" onClick={() => navigate('/production/routes/new')} data-testid="create-route-btn">
          <Plus className="w-4 h-4 mr-1" /> New Route
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search by route name, part name, part number..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="h-8 text-xs pl-7"
              data-testid="routes-search"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-center text-xs text-muted-foreground py-8">Loading...</p>
      ) : routes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <ClipboardList className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {search ? 'No routes match your search' : 'No routes defined yet'}
            </p>
            {!search && (
              <Button size="sm" className="mt-4" onClick={() => navigate('/production/routes/new')}>Create First Route</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-xs" data-testid="routes-table">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2.5 font-medium text-muted-foreground">Route Name</th>
                    <th className="text-left p-2.5 font-medium text-muted-foreground">Part Name</th>
                    <th className="text-left p-2.5 font-medium text-muted-foreground">Part #</th>
                    <th className="text-center p-2.5 font-medium text-muted-foreground">RC#</th>
                    <th className="text-center p-2.5 font-medium text-muted-foreground">BOM Items</th>
                    <th className="text-center p-2.5 font-medium text-muted-foreground">Outputs</th>
                    <th className="text-center p-2.5 font-medium text-muted-foreground">Operations</th>
                    <th className="text-center p-2.5 font-medium text-muted-foreground">Rev</th>
                    <th className="text-center p-2.5 font-medium text-muted-foreground">Status</th>
                    <th className="text-right p-2.5 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map(route => (
                    <tr key={route.id} className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/production/routes/${route.id}`)}
                      data-testid={`route-row-${route.id}`}
                    >
                      <td className="p-2.5 font-medium">{route.route_name}</td>
                      <td className="p-2.5">
                        <button
                          onClick={e => { e.stopPropagation(); route.item_id && navigate(`/inventory/items/${route.item_id}`); }}
                          className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                          data-testid={`route-item-link-${route.id}`}
                        >
                          <EntityLink type="item" id={route.item_id}>{route.item_name || '-'}</EntityLink>
                        </button>
                      </td>
                      <td className="p-2.5 font-mono text-muted-foreground">{route.item_unique_id || '-'}</td>
                      <td className="p-2.5 text-center">
                        {route.route_card_number ? (
                          <Badge variant="outline" className="text-[10px] font-mono">{route.route_card_number}</Badge>
                        ) : '-'}
                      </td>
                      <td className="p-2.5 text-center">
                        <Badge variant="secondary" className="text-[10px]">{(route.bill_of_materials || []).length}</Badge>
                      </td>
                      <td className="p-2.5 text-center">
                        <Badge variant="secondary" className="text-[10px]">{(route.output_items || []).length}</Badge>
                      </td>
                      <td className="p-2.5 text-center">
                        <Badge variant="secondary" className="text-[10px]">{(route.operations || []).length}</Badge>
                      </td>
                      <td className="p-2.5 text-center text-muted-foreground">{route.revision || '0'}</td>
                      <td className="p-2.5 text-center">
                        <Badge variant="outline" className={`text-[10px] ${route.status === 'suspended' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-emerald-50 text-emerald-600 border-emerald-200'}`}>
                          {route.status || 'active'}
                        </Badge>
                      </td>
                      <td className="p-2.5 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" title="Edit"
                            onClick={e => { e.stopPropagation(); navigate(`/production/routes/${route.id}`); }}
                            data-testid={`edit-route-${route.id}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" title={route.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                            onClick={e => { e.stopPropagation(); handleSuspend(route); }}
                            data-testid={`suspend-route-${route.id}`}
                          >
                            {route.status === 'suspended' ? <PlayCircle className="w-3 h-3 text-emerald-600" /> : <PauseCircle className="w-3 h-3 text-amber-600" />}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" title="Delete"
                            onClick={e => { e.stopPropagation(); setDeleteTarget(route); }}
                            data-testid={`delete-route-${route.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({total} routes)</p>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
                  <ChevronLeft className="w-3 h-3 mr-1" /> Prev
                </Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
                  Next <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Route</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.route_name}</strong>?
              This cannot be undone. If work orders reference this route, deletion will be blocked — use Suspend instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-route">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
